import {
  useQuery,
  type ApolloError,
  type WatchQueryFetchPolicy,
} from "@apollo/client";
import { GET_SCOPES } from "../../Findings/FindingsFilters/FindingsFilterApi";
import { useEffect, useMemo } from "react";
import {
  parseScopeHierarchy,
  scopeHierarchyToRoot,
  stripUnscopedNode,
} from "../../Findings/FindingsFilters/FilterUtils";
import useLogger, {
  LOG_SEVERITY,
  SCOPES_HIERARCHY_UNUSABLE,
  SCOPES_QUERY_ERROR,
  scrubbedApolloErrorExtra,
} from "../../Logger/useLogger";
import type { ScopeNode, SelectedScope } from "./ScopeProvider/ScopeProvider";

// GraphQL response shape for GET_SCOPES (hand-written next to its hook).
// `scope_hierarchy.scope_hierarchy` is a JSON-encoded string parsed by
// `parseScopeHierarchy`.
export interface ScopesData {
  scope_hierarchy?: {
    scope_hierarchy?: string | null;
  } | null;
}

// The `scopesList` 2-tuple:
//   [0] the parsed raw scope hierarchy nodes (`ScopeNode[]`)
//   [1] the synthesized "All Resources" root option (`SelectedScope | null`)
export type ScopesList = [ScopeNode[], SelectedScope | null];

export const SCOPES_ERROR_MESSAGE = "Couldn't load scopes. Try again later";

export interface UseScopesDataOptions {
  // Display-only: the query always fetches include_unscoped: true so all pages share one cache entry.
  withUnscoped?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
}

export interface UseScopesDataReturn {
  isLoadingScopesData: boolean;
  scopesData?: ScopesData;
  scopesList: false | ScopesList;
  root?: SelectedScope | null;
  scopesError?: ApolloError;
  hasScopesError: boolean;
}

const useScopesData = ({
  withUnscoped = false,
  fetchPolicy = "cache-first",
}: UseScopesDataOptions): UseScopesDataReturn => {
  const { logError } = useLogger();

  const {
    data: scopesData,
    loading: isLoadingScopesData,
    error: scopesError,
  } = useQuery<ScopesData>(GET_SCOPES, {
    variables: { include_unscoped: true },
    fetchPolicy,
    errorPolicy: "all",
  });

  const rawHierarchy = scopesData?.scope_hierarchy?.scope_hierarchy;

  const parsedHierarchy = useMemo(
    () => parseScopeHierarchy(rawHierarchy),
    [rawHierarchy]
  );

  const visibleHierarchy = useMemo(
    () =>
      withUnscoped || !parsedHierarchy
        ? parsedHierarchy
        : stripUnscopedNode(parsedHierarchy),
    [parsedHierarchy, withUnscoped]
  );

  const scopesList = useMemo<false | ScopesList>(() => {
    if (!rawHierarchy) {
      return false;
    }

    const scopes = visibleHierarchy ?? [];

    return [scopes, scopeHierarchyToRoot(scopes)];
  }, [rawHierarchy, visibleHierarchy]);

  const root = scopesList ? scopesList[1] : undefined;

  const hasUsableScopes = !!scopesList && !!scopesList[0]?.length;

  // An empty-but-parseable hierarchy is a legitimate empty state, not a failure.
  const hasScopesError =
    !isLoadingScopesData && (!!scopesError || !!scopesData) && !parsedHierarchy;

  // Logs the GraphQL query failure.
  useEffect(() => {
    if (!scopesError) {
      return;
    }

    logError(new Error(SCOPES_QUERY_ERROR), SCOPES_QUERY_ERROR, {
      severity: LOG_SEVERITY.CRITICAL,
      tags: { feature: "scope_hierarchy", withUnscoped },
      extra: {
        operation: "get_scope_hierarchy",
        hasPartialData: hasUsableScopes,
        ...scrubbedApolloErrorExtra(scopesError),
      },
    });
  }, [scopesError]);

  // No usable tree; scopesError in deps defers logging to the effect above.
  useEffect(() => {
    if (scopesError || !hasScopesError) {
      return;
    }

    logError(new Error(SCOPES_HIERARCHY_UNUSABLE), SCOPES_HIERARCHY_UNUSABLE, {
      severity: LOG_SEVERITY.CRITICAL,
      tags: { feature: "scope_hierarchy", withUnscoped },
      extra: { operation: "get_scope_hierarchy" },
    });
  }, [scopesError, hasScopesError]);

  return {
    isLoadingScopesData,
    scopesData,
    scopesList,
    root,
    scopesError,
    hasScopesError,
  };
};

export default useScopesData;
